import { useFormik } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import * as Yup from 'yup'
import {
  useAddChecklistMutation,
  useDeleteChecklistMutation,
  useGetTaskTemplatesQuery,
  useLazyGetTaskDetailQuery,
  useUpdateTaskTemplateMutation,
} from 'apiv2'
import { prepareCommonSelects } from 'helpers/prepares'
import { allTasksSelector } from 'store/projects'
import { getTaskDetailAction } from 'store/tasks'
import Button from 'ui/components/buttons/Button'
import { ButtonGroup } from 'ui/components/buttons/Button/styled'
import Select from 'ui/components/forms/Select'
import ModalBasic from 'ui/components/modals/ModalBasic'
import { prepareTaskTemplatePayload } from 'ui/components/modals/ModalTemplates/helpers'
import { notifyError, notifySuccess } from 'ui/components/Notify'
import { TASK_TYPE } from 'utils/constants'
import {
  ChecklistTransferModalTitle,
  ChecklistTransferModalText,
} from './styled'
import {
  CHECKLIST_TRANSFER_TYPE,
  CHECKLIST_VARIANT,
  MAX_CHECKLISTS,
} from '../../constants'
import {
  toFormikChecklists,
  prepareChecklistPointsForTransfer,
} from '../../helpers'

const ChecklistTransferModal = ({
  type,
  variant = CHECKLIST_VARIANT.TASK,
  open,
  checklist,
  defaultTaskId,
  defaultTemplateId,
  onMoveSuccess,
  handlerClose,
}) => {
  const dispatch = useDispatch()
  const taskList = useSelector(allTasksSelector)
  const isTaskVariant = variant === CHECKLIST_VARIANT.TASK
  const isTemplateVariant = variant === CHECKLIST_VARIANT.TEMPLATE
  const [addChecklist] = useAddChecklistMutation()
  const [deleteChecklist] = useDeleteChecklistMutation()
  const [triggerGetTaskDetail] = useLazyGetTaskDetailQuery()
  const [updateTaskTemplate] = useUpdateTaskTemplateMutation()
  const { data: templates = [] } = useGetTaskTemplatesQuery(undefined, {
    skip: !isTemplateVariant,
  })

  const isCopyMode = type === CHECKLIST_TRANSFER_TYPE.COPY
  const isMoveMode = type === CHECKLIST_TRANSFER_TYPE.MOVE
  const destinationTasks = taskList.filter(
    (task) => task.id !== defaultTaskId && task.task_type !== TASK_TYPE.Epic
  )
  const destinationTemplates = templates.filter(
    (template) => template.id !== defaultTemplateId
  )

  const transferTemplateChecklist = async (targetTemplateId) => {
    const targetTemplate = templates.find(
      (template) => template.id === targetTemplateId
    )
    const sourceTemplate = templates.find(
      (template) => template.id === defaultTemplateId
    )
    const targetChecklists = targetTemplate?.checklists ?? []

    if (!targetTemplate || !sourceTemplate) {
      notifyError(
        `Не удалось ${isCopyMode ? 'скопировать' : 'переместить'} чек-лист`
      )
      handlerClose()
      return
    }

    if (targetChecklists.length >= MAX_CHECKLISTS) {
      notifyError(
        `Нельзя ${isCopyMode ? 'скопировать' : 'переместить'} чек-лист: в целевом шаблоне достигнут лимит чек-листов`
      )
      return
    }

    const updatedTargetChecklists = [
      ...toFormikChecklists(targetChecklists),
      ...prepareChecklistPointsForTransfer(checklist),
    ]

    try {
      await updateTaskTemplate({
        templateId: targetTemplateId,
        params: prepareTaskTemplatePayload({
          ...targetTemplate,
          checklists: updatedTargetChecklists,
        }),
      }).unwrap()

      if (isMoveMode) {
        const updatedSourceChecklists = (
          sourceTemplate.checklists ?? []
        ).filter((item) => item.id !== checklist.id)

        await updateTaskTemplate({
          templateId: defaultTemplateId,
          params: prepareTaskTemplatePayload({
            ...sourceTemplate,
            checklists: updatedSourceChecklists,
          }),
        }).unwrap()

        onMoveSuccess()
      }

      notifySuccess(
        isCopyMode ? 'Чек-лист скопирован' : 'Чек-лист перемещён'
      )
    } catch (error) {
      notifyError(
        `Не удалось ${isCopyMode ? 'скопировать' : 'переместить'} чек-лист`
      )
    } finally {
      handlerClose()
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      task: null,
    },
    validationSchema: Yup.object({
      task: Yup.object({
        id: Yup.number().required(),
        value: Yup.string().required(),
        label: Yup.string().required(),
      })
        .required('Нужно заполнить')
        .typeError('Нужно заполнить'),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (isTemplateVariant) {
        await transferTemplateChecklist(values.task.id)
        resetForm()
        return
      }

      const targetTaskId = values.task.id
      const checlistId = checklist.id

      const targetTask = await triggerGetTaskDetail({
        id: targetTaskId,
      }).unwrap()
      const targetChecklists = targetTask?.checklist
      const isChecklistLimitReached =
        targetChecklists?.length === MAX_CHECKLISTS

      if (isChecklistLimitReached) {
        notifyError(
          `Нельзя ${isCopyMode ? 'скопировать' : 'переместить'} чек-лист: в целевой задаче достигнут лимит чек-листов`
        )
      } else {
        const mergedItems = [
          ...toFormikChecklists(targetChecklists),
          ...prepareChecklistPointsForTransfer(checklist),
        ]
        if (isCopyMode) {
          try {
            await addChecklist({
              taskId: targetTaskId,
              data: {
                items: mergedItems,
              },
            }).unwrap()
            notifySuccess('Чек-лист скопирован')
          } catch (error) {
            notifyError('Не удалось скопировать чек-лист')
          } finally {
            handlerClose()
          }
        }
        if (isMoveMode) {
          try {
            await addChecklist({
              taskId: targetTaskId,
              data: {
                items: mergedItems,
              },
            }).unwrap()
            if (Boolean(checklist?.id)) {
              await deleteChecklist({
                itemId: checlistId,
              }).unwrap()
            }
            notifySuccess('Чек-лист перемещён')
            dispatch(getTaskDetailAction(defaultTaskId))
            onMoveSuccess()
          } catch (error) {
            notifyError('Не удалось переместить чек-лист')
          } finally {
            handlerClose()
          }
        }
        resetForm(defaultTaskId)
      }
    },
  })

  return (
    <ModalBasic open={open} handlerClose={handlerClose}>
      <ChecklistTransferModalTitle>
        {isCopyMode ? 'Копировать чек-лист' : 'Переместить чек-лист'}
      </ChecklistTransferModalTitle>
      <ChecklistTransferModalText>
        {isTaskVariant
          ? 'Выберите задачу, в которую'
          : 'Выберите шаблон, в который'}{' '}
        нужно {isCopyMode ? 'скопировать' : 'перенести'} чек-лист.
      </ChecklistTransferModalText>
      <form onSubmit={formik.handleSubmit} noValidate>
        <Select
          label={isTaskVariant ? 'Задача' : 'Шаблон'}
          placeholder={isTaskVariant ? 'Не выбрана' : 'Не выбран'}
          options={prepareCommonSelects(
            isTaskVariant ? destinationTasks : destinationTemplates,
            true
          )}
          value={
            formik.getFieldMeta('task')
              ? formik.getFieldMeta('task').value
              : null
          }
          onChange={(newValue) => {
            if (newValue) {
              formik.setFieldValue('task', newValue)
            } else {
              formik.setFieldValue('task', null)
            }
          }}
          meta={formik.getFieldMeta('task')}
          error={
            formik.getFieldMeta('task').touched &&
            formik.getFieldMeta('task').error
          }
          maxHeight={300}
          isDisabled={formik.isSubmitting}
        />

        <ButtonGroup>
          <Button
            title={isCopyMode ? 'Копировать' : 'Переместить'}
            type="submit"
            loading={formik.isSubmitting}
            disabled={formik.isSubmitting}
          />
          <Button
            title="Отмена"
            theme="borderGrey"
            type="reset"
            disabled={formik.isSubmitting}
            onClick={() => {
              handlerClose()
              formik.resetForm()
            }}
          />
        </ButtonGroup>
      </form>
    </ModalBasic>
  )
}

export default ChecklistTransferModal
